import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.echomemoir.ai/api';
const REDEEM_URL = 'https://echomemoir.ai/redeem';
const QR_API = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&color=8b5cf6&bgcolor=ffffff&data=${encodeURIComponent(REDEEM_URL)}`;

const GiftSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get('code');
  const printRef = useRef(null);

  const [giftData, setGiftData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!code) { setLoading(false); return; }
    const fetchGift = async () => {
      try {
        const res = await fetch(`${API_URL}/billing/gift/${code}`);
        const data = await res.json();
        if (data.code) setGiftData(data);
      } catch (err) {
        console.error('Gift lookup error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGift();
  }, [code]);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    const printWindow = window.open('', '_blank', 'width=600,height=900');
    printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>EchoMemoir Gift Card</title>
  <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'DM Sans', sans-serif;
      display: flex; justify-content: center; align-items: center;
      min-height: 100vh; background: #f5f3ee; padding: 20px;
    }
    .gift-card {
      width: 480px; background: #fff; border-radius: 20px;
      overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    }
    .card-header {
      background: linear-gradient(135deg, #1a1430, #08060e);
      padding: 32px 32px 28px; text-align: center;
    }
    .card-logo {
      font-family: 'Cormorant Garamond', Georgia, serif;
      font-size: 20px; font-weight: 500; color: rgba(255,255,255,0.6);
      margin-bottom: 16px; letter-spacing: 1px;
    }
    .card-title { font-size: 26px; font-weight: 700; color: #fff; margin-bottom: 6px; }
    .card-plan { font-size: 14px; color: #a78bfa; font-weight: 500; }
    .card-body { padding: 28px 32px 32px; text-align: center; }
    .card-code-label {
      font-size: 11px; text-transform: uppercase; letter-spacing: 2px;
      color: #999; margin-bottom: 8px;
    }
    .card-code {
      font-size: 32px; font-weight: 700; letter-spacing: 4px;
      color: #8b5cf6; font-family: monospace; margin-bottom: 20px;
    }
    .card-qr { margin-bottom: 16px; }
    .card-qr img { width: 140px; height: 140px; border-radius: 12px; border: 2px solid #f0ecf9; }
    .card-scan-label { font-size: 12px; color: #999; margin-bottom: 24px; }
    .card-message {
      background: #faf9f7; border-left: 3px solid #d4a017;
      border-radius: 0 8px 8px 0; padding: 14px 18px;
      margin-bottom: 20px; text-align: left;
    }
    .card-message-from { font-size: 11px; color: #999; margin-bottom: 4px; }
    .card-message-text { font-size: 14px; color: #444; font-style: italic; line-height: 1.6; }
    .card-footer {
      font-size: 12px; color: #bbb; padding-top: 16px;
      border-top: 1px solid #f0ece4;
    }
    .card-footer a { color: #8b5cf6; text-decoration: none; }
    @media print {
      body { background: #fff; padding: 0; }
      .gift-card { box-shadow: none; }
    }
  </style>
</head>
<body>
  ${printContent.innerHTML}
  <script>setTimeout(() => { window.print(); }, 600);<\/script>
</body>
</html>`);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#08060e', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)' }}>
        Loading...
      </div>
    );
  }

  if (!code || !giftData) {
    return (
      <div style={{ minHeight: '100vh', background: '#08060e', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff', padding: '20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎁</div>
        <h2 style={{ marginBottom: '8px' }}>Gift code not found</h2>
        <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '24px' }}>The gift code may still be processing. Check your email for confirmation.</p>
        <button onClick={() => navigate('/')} style={{ padding: '12px 24px', borderRadius: '10px', border: 'none', background: 'rgba(139,92,246,0.2)', color: '#a78bfa', fontSize: '14px', cursor: 'pointer' }}>
          Go Home
        </button>
      </div>
    );
  }

  const planEmoji = giftData.tier === 'legacy_vault' ? '🏛️' : '🌳';

  return (
    <div style={{
      minHeight: '100vh', background: '#08060e', color: '#fff',
      padding: '60px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>

        {/* Success icon */}
        <div style={{
          width: '80px', height: '80px', borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(34,197,94,0.2), rgba(16,185,129,0.1))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px', fontSize: '40px'
        }}>🎁</div>

        <h1 style={{ fontSize: '26px', fontWeight: '700', marginBottom: '8px' }}>Your gift is ready!</h1>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', lineHeight: '1.6', marginBottom: '32px' }}>
          Payment confirmed. {giftData.recipientName ? `Share this code with ${giftData.recipientName}` : 'Share this code with your loved one'} — they can redeem it at any time.
        </p>

        {/* Gift code card */}
        <div style={{
          background: 'rgba(251,191,36,0.06)', border: '2px dashed rgba(251,191,36,0.25)',
          borderRadius: '20px', padding: '32px 24px', marginBottom: '24px'
        }}>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '10px' }}>
            Gift Code
          </p>
          <p style={{ color: '#fbbf24', fontSize: '32px', fontWeight: '700', letterSpacing: '4px', fontFamily: 'monospace', marginBottom: '14px' }}>
            {giftData.code}
          </p>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginBottom: '8px' }}>
            {planEmoji} {giftData.planName} — {giftData.durationMonths} months
          </p>

          {/* QR code */}
          <div style={{ marginBottom: '16px' }}>
            <img src={QR_API} alt="Scan to redeem" style={{ width: '120px', height: '120px', borderRadius: '10px', background: '#fff', padding: '4px' }} />
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', marginTop: '6px' }}>
              Scan to redeem at echomemoir.ai/redeem
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button onClick={handleCopy} style={{
              padding: '10px 20px', borderRadius: '10px',
              border: '1px solid rgba(251,191,36,0.3)',
              background: copied ? 'rgba(34,197,94,0.15)' : 'rgba(251,191,36,0.1)',
              color: copied ? '#4ade80' : '#fbbf24',
              fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s ease'
            }}>
              {copied ? '✓ Copied!' : '📋 Copy Code'}
            </button>
            <button onClick={handlePrint} style={{
              padding: '10px 20px', borderRadius: '10px',
              border: '1px solid rgba(139,92,246,0.3)',
              background: 'rgba(139,92,246,0.1)', color: '#a78bfa',
              fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s ease'
            }}>
              🖨 Print Gift Card
            </button>
          </div>
        </div>

        {/* Gift message preview */}
        {giftData.giftMessage && (
          <div style={{
            background: 'rgba(255,255,255,0.03)', borderLeft: '3px solid #fbbf24',
            borderRadius: '0 10px 10px 0', padding: '14px 18px',
            marginBottom: '24px', textAlign: 'left'
          }}>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', marginBottom: '6px' }}>Your message:</p>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontStyle: 'italic', lineHeight: '1.6', margin: 0 }}>
              "{giftData.giftMessage}"
            </p>
          </div>
        )}

        {/* Info */}
        <div style={{
          background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)',
          borderRadius: '12px', padding: '16px', marginBottom: '28px', textAlign: 'left'
        }}>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', lineHeight: '1.7', margin: 0 }}>
            📧 A confirmation email with the gift code has been sent to you.
            {giftData.recipientName && ' We also sent a notification to the recipient.'}
            <br /><br />
            The recipient can redeem the code at <strong style={{ color: '#a78bfa' }}>echomemoir.ai/redeem</strong> after creating a free account.
          </p>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button onClick={() => navigate('/gift')} style={{
            padding: '12px 20px', borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.1)',
            background: 'transparent', color: 'rgba(255,255,255,0.5)', fontSize: '14px', cursor: 'pointer'
          }}>Buy Another</button>
          <button onClick={() => navigate('/')} style={{
            padding: '12px 20px', borderRadius: '10px', border: 'none',
            background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
            color: '#fff', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
          }}>Go to EchoMemoir</button>
        </div>
      </div>

      {/* Hidden printable gift card */}
      <div ref={printRef} style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div className="gift-card">
          <div className="card-header">
            <div className="card-logo">✦ EchoMemoir</div>
            <div className="card-title">🎁 A Gift For You</div>
            <div className="card-plan">{planEmoji} {giftData.planName} — {giftData.durationMonths} Months</div>
          </div>
          <div className="card-body">
            <div className="card-code-label">Your Gift Code</div>
            <div className="card-code">{giftData.code}</div>
            <div className="card-qr">
              <img src={QR_API} alt="Scan to redeem" />
            </div>
            <div className="card-scan-label">Scan or visit echomemoir.ai/redeem</div>
            {giftData.giftMessage && (
              <div className="card-message">
                <div className="card-message-from">A message from {giftData.purchaserName || 'someone special'}:</div>
                <div className="card-message-text">"{giftData.giftMessage}"</div>
              </div>
            )}
            <div className="card-footer">
              Redeem at <a href="https://echomemoir.ai/redeem">echomemoir.ai/redeem</a> · Valid for 1 year
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GiftSuccess;
